import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, MapPin, Users, Trophy, CalendarDays } from 'lucide-react';
import { useUserGroupInfo } from '@/hooks/useUserGroupInfo';
import { useLeagueMatches } from '@/hooks/useLeagueMatches';
import { useTennisEncounterForPreview } from '@/hooks/useTennisEncounterForPreview';
import { useTennisChallengeEncounters } from '@/hooks/useTennisChallengeEncounters';
import { parseLocalDate } from '@/utils/dateUtils';
import TennisChallengeCard from '@/components/league/TennisChallengeCard';
import GroupScheduleMatrix from '@/components/tournament/GroupScheduleMatrix';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import type { MyEvent } from '@/hooks/useMyEvents';

interface UpcomingEventPreviewProps {
  events: MyEvent[];
  onEventClick: (event: MyEvent) => void;
}

const formatTo12h = (time: string): string => {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m || '00'} ${ampm}`;
};

/** Single event card content – extracted to keep the carousel wrapper clean */
const EventSlide = ({ event, onEventClick }: { event: MyEvent; onEventClick: (e: MyEvent) => void }) => {
  const [countdown, setCountdown] = useState('');
  const [countdownLabel, setCountdownLabel] = useState('');

  const isTournament = !event.isLeague;
  const { groupInfo, loading: groupLoading } = useUserGroupInfo(isTournament ? event.id : '');
  const { userMatchForEncounter, userTeam, opponentTeam, isLoading: leagueLoading, matchesByEncounter, setSelectedEncounter } = useLeagueMatches(
    event.isLeague ? event.id : ''
  );
  const { encounterInfo, isTennis } = useTennisEncounterForPreview(
    event.id, event.sport || '', !!event.isLeague, event.startDate
  );
  const { userEncountersByWeek } = useTennisChallengeEncounters(
    isTennis ? event.id : ''
  );

  // Get the tennis challenge for the current encounter
  const tennisChallenge = isTennis && encounterInfo 
    ? userEncountersByWeek[encounterInfo.encounterNumber] 
    : null;
  useEffect(() => {
    if (!event.isLeague || !matchesByEncounter || Object.keys(matchesByEncounter).length === 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const encounters = Object.entries(matchesByEncounter)
      .map(([enc, matches]) => ({
        encounter: Number(enc),
        date: matches.find(m => m.scheduled_date)?.scheduled_date
      }))
      .filter(e => e.date)
      .sort((a, b) => a.encounter - b.encounter);

    if (encounters.length === 0) return;

    const next = encounters.find(e => {
      const d = parseLocalDate(e.date!);
      return d >= today;
    });

    const targetEncounter = next?.encounter ?? encounters[encounters.length - 1].encounter;
    setSelectedEncounter(targetEncounter);
  }, [event.isLeague, matchesByEncounter, setSelectedEncounter]);

  // Countdown timer
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      let eventDate: Date;

      // For Tennis leagues, use the encounter week start date
      if (isTennis && encounterInfo) {
        eventDate = new Date(encounterInfo.weekStartDate);
      } else {
        const dateStr = (event.isLeague && userMatchForEncounter?.scheduled_date) 
          ? userMatchForEncounter.scheduled_date 
          : event.startDate;
        eventDate = parseLocalDate(dateStr);
      }
      
      if (event.time && !isTennis) {
        const [h, m] = event.time.split(':');
        eventDate.setHours(parseInt(h, 10), parseInt(m || '0', 10), 0, 0);
      }

      const diff = eventDate.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCountdown(isTennis ? 'Esta semana' : '¡Hoy!');
        setCountdownLabel('');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setCountdown(`${days}`);
        setCountdownLabel(days === 1 ? 'día' : 'días');
      } else if (hours > 0) {
        setCountdown(`${hours}`);
        setCountdownLabel(hours === 1 ? 'hora' : 'horas');
      } else {
        setCountdown(`${minutes}`);
        setCountdownLabel('min');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [event.startDate, event.time, event.isLeague, userMatchForEncounter, isTennis, encounterInfo]);

  const formattedDate = useMemo(() => {
    // For Tennis leagues, show the date range from the encounter info
    if (isTennis && encounterInfo) {
      return encounterInfo.dateRangeLabel;
    }
    const dateStr = (event.isLeague && userMatchForEncounter?.scheduled_date)
      ? userMatchForEncounter.scheduled_date
      : event.startDate;
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  }, [event.startDate, event.isLeague, userMatchForEncounter, isTennis, encounterInfo]);

  return (
    <Card 
      className="overflow-hidden border-0 shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300"
      onClick={() => onEventClick(event)}
    >
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-[#1b3521] via-[#31633f] to-[#1b3521] p-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" 
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(231,209,182,0.3) 0%, transparent 50%)' }} 
        />
        
        <div className="relative flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-[#e7d1b6]/20 text-[#e7d1b6] border-[#e7d1b6]/30 text-[10px] md:text-xs font-medium">
                {event.isLeague ? '🏆 Liga' : '🎾 Torneo'}
              </Badge>
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] md:text-xs">
                Próximo
              </Badge>
              {isTennis && encounterInfo && (
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] md:text-xs font-medium">
                  Encuentro {encounterInfo.encounterNumber}
                </Badge>
              )}
            </div>

            <h3 className="text-base md:text-lg font-bold text-white truncate">
              {event.title}
            </h3>
            
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-[#e7d1b6]/80 text-xs md:text-sm">
                <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="capitalize">{formattedDate}</span>
              </div>
              {event.time && !isTennis && (
                <div className="flex items-center gap-1.5 text-[#e7d1b6]/80 text-xs md:text-sm">
                  <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{formatTo12h(event.time)}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-[#e7d1b6]/80 text-xs md:text-sm">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{event.location}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {event.category && (
                <span className="text-[10px] md:text-xs bg-[#31633f]/50 text-[#e7d1b6]/90 px-2 py-0.5 rounded-full">
                  {event.category}
                </span>
              )}
              {event.partner && (
                <span className="text-[10px] md:text-xs text-[#e7d1b6]/70 flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {event.partner}
                </span>
              )}
            </div>
          </div>

          {/* Countdown */}
          <div className="flex flex-col items-center justify-center bg-[#e7d1b6]/10 backdrop-blur-sm rounded-xl px-3 py-2 md:px-4 md:py-3 border border-[#e7d1b6]/20 min-w-[60px]">
            {(countdown === '¡Hoy!' || countdown === 'Esta semana') ? (
              <span className="text-sm md:text-base font-bold text-[#e7d1b6] animate-pulse">
                {countdown}
              </span>
            ) : (
              <>
                <span className="text-2xl md:text-3xl font-bold text-[#e7d1b6] leading-none">
                  {countdown}
                </span>
                <span className="text-[10px] md:text-xs text-[#e7d1b6]/70 mt-0.5">
                  {countdownLabel}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content area */}
      <CardContent className="p-0">
        {isTournament && !groupLoading && groupInfo && (
          <div className="p-3 md:p-4" onClick={(e) => e.stopPropagation()}>
            <GroupScheduleMatrix groupInfo={groupInfo} />
          </div>
        )}
        {isTournament && groupLoading && (
          <div className="p-4">
            <div className="h-24 bg-muted rounded-lg animate-pulse" />
          </div>
        )}
        {isTournament && !groupLoading && !groupInfo && (
          <div className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Grupos pendientes de asignación</p>
          </div>
        )}

        {event.isLeague && !leagueLoading && userMatchForEncounter && (
          <div className="p-3 md:p-4">
            {isTennis && tennisChallenge ? (
              <TennisChallengeCard 
                encounter={tennisChallenge} 
                weekNumber={encounterInfo!.encounterNumber} 
                dateLabel={encounterInfo!.dateRangeLabel} 
              />
            ) : (
              <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-3 md:p-4 border border-primary/10">
                <p className="text-xs font-semibold text-primary mb-2">
                  {isTennis && encounterInfo 
                    ? `Encuentro ${encounterInfo.encounterNumber} · ${encounterInfo.dateRangeLabel}`
                    : 'Próximo encuentro'}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <p className="text-xs md:text-sm font-semibold text-foreground">{userTeam?.player1 || 'Tú'}</p>
                    {userTeam?.player2 && <p className="text-[10px] md:text-xs text-muted-foreground">{userTeam.player2}</p>}
                  </div>
                  <div className="mx-3 flex flex-col items-center">
                    <span className="text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5">VS</span>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-xs md:text-sm font-semibold text-foreground">{opponentTeam?.player1 || 'Oponente'}</p>
                    {opponentTeam?.player2 && <p className="text-[10px] md:text-xs text-muted-foreground">{opponentTeam.player2}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3 mt-2 text-[10px] md:text-xs text-muted-foreground">
                  {userMatchForEncounter.scheduled_date && (
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {parseLocalDate(userMatchForEncounter.scheduled_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                  {userMatchForEncounter.court_number && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      Cancha {userMatchForEncounter.court_number}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {event.isLeague && leagueLoading && (
          <div className="p-4">
            <div className="h-20 bg-muted rounded-lg animate-pulse" />
          </div>
        )}
        {event.isLeague && !leagueLoading && !userMatchForEncounter && (
          <div className="p-3 md:p-4">
            {isTennis && tennisChallenge && encounterInfo ? (
              <TennisChallengeCard 
                encounter={tennisChallenge} 
                weekNumber={encounterInfo.encounterNumber} 
                dateLabel={encounterInfo.dateRangeLabel} 
              />
            ) : isTennis && encounterInfo ? (
              <p className="text-sm text-muted-foreground text-center">
                Encuentro {encounterInfo.encounterNumber} · {encounterInfo.dateRangeLabel}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground text-center">Partidos pendientes de programación</p>
            )}
          </div>
        )}

        <div className="px-4 py-2 bg-muted/30 border-t border-border/50 flex items-center justify-center gap-1.5">
          <span className="text-[10px] md:text-xs text-muted-foreground">Toca para ver detalles</span>
          <Trophy className="h-3 w-3 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
};

const UpcomingEventPreview = ({ events, onEventClick }: UpcomingEventPreviewProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const syncHeight = useCallback(() => {
    if (!viewportRef.current) return;
    const idx = api?.selectedScrollSnap() ?? 0;
    const slide = slideRefs.current[idx];
    if (slide) {
      viewportRef.current.style.height = `${slide.scrollHeight + 8}px`;
    }
  }, [api]);

  // Capture the Embla viewport (the overflow-hidden div)
  useEffect(() => {
    if (!api) return;
    const rootNode = api.rootNode() as HTMLDivElement | null;
    if (rootNode) {
      viewportRef.current = rootNode;
      rootNode.style.transition = 'height 0.3s ease';
    }
    setCurrent(api.selectedScrollSnap());
    syncHeight();
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      syncHeight();
    };
    api.on('select', onSelect);
    return () => { api.off('select', onSelect); };
  }, [api, syncHeight]);

  // ResizeObserver to handle async content loading
  useEffect(() => {
    const observers: ResizeObserver[] = [];
    slideRefs.current.forEach((slide) => {
      if (!slide) return;
      const ro = new ResizeObserver(() => syncHeight());
      ro.observe(slide);
      observers.push(ro);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [events.length, syncHeight]);

  const showCarousel = events.length > 1;

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <div className="h-1 w-1 rounded-full bg-[#e7d1b6]" />
        <h2 className="text-sm md:text-base font-semibold" style={{ color: '#e7d1b6' }}>
          {events.length > 1 ? 'Tus próximos eventos' : 'Tu próximo evento'}
        </h2>
      </div>

      {showCarousel ? (
        <>
          <Carousel setApi={setApi} opts={{ align: 'start', loop: false }}>
            <CarouselContent className="-ml-3 items-start">
              {events.map((event, idx) => (
                <CarouselItem key={event.id} className="pl-3 basis-full h-auto">
                  <div ref={(el) => { slideRefs.current[idx] = el; }}>
                    <EventSlide event={event} onEventClick={onEventClick} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* Pagination dots */}
          <div className="flex justify-center gap-1.5 pt-1">
            {events.map((_, idx) => (
              <button
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === current ? 'w-4 bg-[#e7d1b6]' : 'w-1.5 bg-[#e7d1b6]/30'
                }`}
                onClick={() => api?.scrollTo(idx)}
              />
            ))}
          </div>
        </>
      ) : (
        <EventSlide event={events[0]} onEventClick={onEventClick} />
      )}
    </div>
  );
};

export default UpcomingEventPreview;
